import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "ursidlex-history";
const MAX_HISTORY_ITEMS = 50;

export type HistorySource =
  | {
      kind?: "article";
      articleNum: string;
      idcc?: string;
      sectionPath: string;
      sectionIds?: string[];
      articleUrl: string;
      conventionTitle?: string;
      score: number;
    }
  | {
      kind: "decision";
      decisionId: string;
      articleNum: string;
      sectionPath: string;
      articleUrl: string;
      score: number;
      chamber?: string;
      decisionDate?: string;
    };

export interface HistoryItem {
  id: string;
  question: string;
  answer?: string;
  sources?: HistorySource[];
  timestamp: number;
}

export function useHistory() {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as HistoryItem[];
        setHistory(parsed);
      }
    } catch (e) {
      console.error("Failed to load history from localStorage:", e);
    }
  }, []);

  // Save to localStorage whenever history changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (e) {
      console.error("Failed to save history to localStorage:", e);
    }
  }, [history]);

  const addItem = useCallback((question: string) => {
    const newItem: HistoryItem = {
      id: crypto.randomUUID(),
      question,
      timestamp: Date.now(),
    };

    setHistory((prev) => {
      // Add to beginning, remove duplicates, and limit size
      const filtered = prev.filter((item) => item.question !== question);
      return [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    });

    return newItem.id;
  }, []);

  const updateItem = useCallback((id: string, answer: string, sources: HistorySource[]) => {
    setHistory((prev) =>
      prev.map((item) => (item.id === id ? { ...item, answer, sources } : item)),
    );
  }, []);

  const getItem = useCallback(
    (id: string) => {
      return history.find((item) => item.id === id);
    },
    [history],
  );

  const findByQuestion = useCallback(
    (question: string) => {
      return history.find((item) => item.question === question && item.answer);
    },
    [history],
  );

  const removeItem = useCallback((id: string) => {
    setHistory((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  return { history, addItem, updateItem, getItem, findByQuestion, removeItem, clearHistory };
}
